package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.Canvas
import androidx.compose.animation.*
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.*
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.*
import org.koin.compose.koinInject

@Composable
fun GestanteExamsScreen(patientId: Long, onBack: () -> Unit, viewModel: GestanteDetailViewModel = koinInject()) {
    val state by viewModel.state.collectAsState()
    LaunchedEffect(patientId) { viewModel.load(patientId) }
    key(patientId) { GestanteExamsContent(state, onBack, { viewModel.load(patientId) },
        onCreate = { viewModel.createExam(patientId, it) },
        onAddResult = { examId, input -> viewModel.addExamResult(patientId, examId, input) },
        onUpdateResult = { examId, resultId, input -> viewModel.updateExamResult(patientId, examId, resultId, input) },
        onClearFeedback = viewModel::clearActionFeedback) }
}

/** A full-screen dialog keeps the overview, detail and forms on the same source of truth. */
@Composable
internal fun GestanteExamsContent(
    state: GestanteDetailUiState,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onCreate: (GestanteExamInput) -> Unit,
    onAddResult: (Long, GestanteExamResultInput) -> Unit,
    onUpdateResult: (Long, Long, GestanteExamResultInput) -> Unit,
    onClearFeedback: () -> Unit
) {
    var selectedExamId by rememberSaveable { mutableStateOf<Long?>(null) }
    var editor by rememberSaveable { mutableStateOf<String?>(null) }
    var editingResultId by rememberSaveable { mutableStateOf<Long?>(null) }
    var submittedRevision by rememberSaveable { mutableStateOf<Long?>(null) }
    var closingDialog by remember { mutableStateOf(false) }
    val focus = LocalFocusManager.current
    val content = state as? GestanteDetailUiState.Content
    val overviewScroll = rememberLazyListState()
    val selected = content?.detail?.exams?.find { it.id == selectedExamId }
    val canCreate = content != null && !content.saving
    val openCreate = { focus.clearFocus(); onClearFeedback(); editor = "create" }

    LaunchedEffect(content?.actionRevision) {
        if (submittedRevision != null && content != null && content.actionRevision > submittedRevision!!) {
            focus.clearFocus()
            if (selectedExamId == null) closingDialog = true
            else { editor = null; editingResultId = null }
            submittedRevision = null
            // Creation may originate at the bottom of a long list. Reveal the saved record and feedback.
            if (selectedExamId == null) overviewScroll.scrollToItem(0)
        }
    }

    ExamScaffold("Exames", onBack) { modifier ->
        when (state) {
            GestanteDetailUiState.Loading -> ExamLoading(modifier)
            is GestanteDetailUiState.Error -> Column(modifier.padding(PartejarTheme.spacing.space16), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)) {
                ExamAlert(state.message, error = true)
                ExamAction("Tentar novamente", PartejarIcons.Actions.back, onRetry)
            }
            is GestanteDetailUiState.Content -> ExamEntrance(modifier) {
                val summaries = remember(state.detail.exams) { examSummaries(state.detail.exams) }
                LazyColumn(Modifier.fillMaxSize(), state = overviewScroll, contentPadding = PaddingValues(PartejarTheme.spacing.space16), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)) {
                    item(key = "overview") { ExamOverview(examCountLabel(summaries.size), "Consulte os exames realizados e acompanhe seus resultados.") }
                    if (state.actionSuccess != null && editor == null) item(key = "success") { ExamAlert(state.actionSuccess, error = false) }
                    if (summaries.isEmpty()) item(key = "empty") {
                        ExamEmpty("Seus exames, em um só lugar", "Registre um exame para acompanhar os resultados ao longo do cuidado.") {
                            ExamAction("Adicionar exame", PartejarIcons.Actions.add, openCreate, enabled = canCreate, secondary = true)
                        }
                    } else {
                        item(key = "heading") { ExamSectionTitle("EXAMES REALIZADOS") }
                        items(summaries, key = { it.id }) { summary ->
                            ExamSummaryCard(summary, enabled = !state.saving) { onClearFeedback(); selectedExamId = summary.id }
                        }
                        item(key = "add") { ExamAction("Adicionar exame", PartejarIcons.Actions.add, openCreate, enabled = canCreate, secondary = true) }
                    }
                    if (state.detail.examTypes.isEmpty()) item(key = "catalog") {
                        Text("Sem sugestões disponíveis. Você pode informar o nome do exame manualmente.", color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                }
            }
        }
    }

    if (content != null && (selected != null || editor != null)) {
        val visibility = remember { MutableTransitionState(false).apply { targetState = true } }
        visibility.targetState = !closingDialog
        LaunchedEffect(visibility.isIdle, visibility.currentState) {
            if (closingDialog && visibility.isIdle && !visibility.currentState) {
                selectedExamId = null
                editor = null
                editingResultId = null
                submittedRevision = null
                closingDialog = false
            }
        }
        val close: () -> Unit = {
            if (!content.saving && !closingDialog) {
                focus.clearFocus()
                onClearFeedback()
                if (editor != null && selected != null) { editor = null; editingResultId = null; submittedRevision = null }
                else closingDialog = true
            }
        }
        Dialog(onDismissRequest = close, properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnBackPress = !content.saving, dismissOnClickOutside = false)) {
          val dialogFocus = LocalFocusManager.current
          LaunchedEffect(editor, closingDialog) { dialogFocus.clearFocus() }
          PartejarVisibility(visibility) {
            ExamScaffold(
                title = when (editor) { "create" -> "Adicionar exame"; "add" -> "Adicionar resultado"; "edit" -> "Corrigir resultado"; else -> "Detalhe do exame" },
                onBack = close, backEnabled = !content.saving && !closingDialog
            ) { modifier ->
                val reduced = reducedMotion()
                val distance = with(LocalDensity.current) { PartejarTheme.spacing.space12.roundToPx() }
                AnimatedContent(targetState = editor to editingResultId, modifier = modifier,
                    transitionSpec = {
                        if (reduced) (EnterTransition.None togetherWith ExitTransition.None).using(null)
                        else ((fadeIn(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) +
                            slideInVertically(tween(PartejarMotion.entrance, easing = PartejarMotion.enter)) { distance }) togetherWith
                            (fadeOut(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) +
                            slideOutVertically(tween(PartejarMotion.departure, easing = PartejarMotion.exit)) { distance })).using(null)
                    }, label = "examEditor"
                ) { (shownEditor, shownResultId) ->
                        val busy = content.saving || closingDialog || shownEditor != editor || shownResultId != editingResultId
                        when (shownEditor) {
                            "create" -> RegisterExamForm(content.detail.examTypes, content.saving, content.actionError,
                                onSave = { submittedRevision = content.actionRevision; onCreate(it) }, onCancel = close, interactionsEnabled = !busy)
                            "add", "edit" -> if (selected != null) ExamResultForm(
                                initial = selected.results.find { it.id == shownResultId },
                                analyses = content.detail.examTypes.find { it.id == selected.type.id }?.let { type ->
                                    type.analyses.map { if (type.active) it else it.copy(active = false) }
                                }.orEmpty(),
                                saving = content.saving, error = content.actionError,
                                onSave = {
                                    submittedRevision = content.actionRevision
                                    val resultId = shownResultId
                                    if (resultId == null) onAddResult(selected.id, it) else onUpdateResult(selected.id, resultId, it)
                                }, onCancel = close, interactionsEnabled = !busy
                            )
                            else -> if (selected != null) ExamDetailContent(selected, content.detail.exams, busy, content.actionSuccess,
                                onAdd = { focus.clearFocus(); onClearFeedback(); editor = "add" },
                                onEdit = { focus.clearFocus(); onClearFeedback(); editingResultId = it; editor = "edit" })
                        }
                }
            }
          }
        }
    }
}

@Composable
private fun ExamScaffold(title: String, onBack: () -> Unit, backEnabled: Boolean = true, content: @Composable (Modifier) -> Unit) {
    Scaffold(containerColor = MaterialTheme.colorScheme.background, topBar = {
        PartejarTopBar(title = title, leadingContent = { PartejarIconButton(onClick = onBack, enabled = backEnabled) { Icon(PartejarIcons.Actions.back, "Voltar") } })
    }) { padding ->
        content(Modifier.fillMaxSize().padding(padding).consumeWindowInsets(padding).windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Horizontal + WindowInsetsSides.Bottom)).imePadding())
    }
}

@Composable
internal fun ExamOverview(title: String, description: String) {
    Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12), verticalAlignment = Alignment.Top) {
        Surface(shape = MaterialTheme.shapes.large, color = MaterialTheme.colorScheme.primaryContainer, contentColor = MaterialTheme.colorScheme.onPrimaryContainer) {
            Icon(PartejarIcons.Clinical.exams, null, Modifier.padding(PartejarTheme.spacing.space12).size(PartejarTheme.dimensions.iconLarge))
        }
      Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
        Text(title, style = MaterialTheme.typography.headlineSmall, modifier = Modifier.semantics { heading() })
        Text(description, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
      }
    }
}

@Composable
internal fun ExamSummaryCard(summary: ExamSummary, enabled: Boolean = true, onClick: () -> Unit) {
    PartejarCard(onClick = onClick, modifier = Modifier.fillMaxWidth(), enabled = enabled, motionEnabled = true) {
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12), verticalAlignment = Alignment.Top) {
                Text(summary.name.uppercase(), modifier = Modifier.weight(1f), style = MaterialTheme.typography.titleLarge)
                PartejarStatusBadge(summary.status.toExamLabel(), tone = summary.status.toBadgeTone())
            }
            HorizontalDivider(thickness = PartejarTheme.dimensions.strokeThin, color = MaterialTheme.colorScheme.outlineVariant)
            Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                Text(summary.date, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12), verticalAlignment = Alignment.CenterVertically) {
                    Text(summary.resultsLabel, modifier = Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    Icon(PartejarIcons.Actions.next, null, Modifier.size(PartejarTheme.dimensions.iconMedium), tint = MaterialTheme.colorScheme.primary)
                }
            }
        }
    }
}

@Composable
internal fun ExamDetailContent(exam: GestanteExam, exams: List<GestanteExam>, saving: Boolean, success: String?, onAdd: () -> Unit, onEdit: (Long) -> Unit) {
    val spacing = PartejarTheme.spacing
    LazyColumn(Modifier.fillMaxSize(), contentPadding = PaddingValues(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
        item { ExamOverview(exam.type.name.replace('_', ' '), examDate(exam.performedAt)) }
        item { PartejarStatusBadge(exam.status.toExamLabel(), tone = exam.status.toBadgeTone()) }
        item { Text(examResultsLabel(exam.results.size), color = MaterialTheme.colorScheme.onSurfaceVariant) }
        if (success != null) item { ExamAlert(success, error = false) }
        exam.observation?.takeIf { it.isNotBlank() }?.let { observation -> item { PartejarCard(Modifier.fillMaxWidth()) { Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) { ExamSectionTitle("OBSERVAÇÃO"); Text(observation) } } } }
        if (exam.results.isEmpty()) item { ExamEmpty("Resultados ainda não registrados", "Adicione os resultados quando estiverem disponíveis.") {} }
        else item { ExamSectionTitle("RESULTADOS") }
        items(exam.results, key = { it.id }) { result ->
            val history = remember(exams, exam.id, result) { comparableExamHistory(exams, exam, result) }
            ExamResultCard(result, history, enabled = !saving, onEdit = { onEdit(result.id) })
        }
        item { ExamAction("Adicionar resultado", PartejarIcons.Actions.add, onAdd, enabled = !saving) }
    }
}

@Composable
@OptIn(ExperimentalLayoutApi::class)
internal fun ExamResultCard(result: GestanteExamResult, history: List<ExamHistoryPoint>, enabled: Boolean, onEdit: () -> Unit) {
    val spacing = PartejarTheme.spacing
    PartejarCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(spacing.space12),
                verticalArrangement = Arrangement.spacedBy(spacing.space8)
            ) {
                Text(result.parameter, style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.align(Alignment.CenterVertically).semantics { heading() })
                Text(listOfNotNull(numericExamDisplay(result.value), result.unit?.takeIf { it.isNotBlank() }).joinToString(" "),
                    style = MaterialTheme.typography.headlineSmall, modifier = Modifier.align(Alignment.CenterVertically))
                // Reserve the badge's intrinsic width; use spare space to align it to the end.
                Box(Modifier.weight(1f).align(Alignment.CenterVertically), contentAlignment = Alignment.CenterEnd) {
                    PartejarStatusBadge(result.status.toExamLabel(), tone = result.status.toBadgeTone())
                }
            }
            result.reference?.takeIf { it.isNotBlank() }?.let { Text("Referência: $it", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant) }
            val evolution = examEvolutionState(result, history)
            if (evolution != ExamEvolutionState.NOT_APPLICABLE) {
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    ExamSectionTitle("EVOLUÇÃO")
                    if (evolution == ExamEvolutionState.AVAILABLE) {
                        Text("${history.size} medições comparáveis", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        ExamHistoryTimeline(history, result.unit.orEmpty())
                    } else Text("A evolução estará disponível após pelo menos duas medições comparáveis.",
                        style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
            ExamAction("Corrigir", PartejarIcons.Actions.edit, onEdit, enabled = enabled, secondary = true)
        }
    }
}

@Composable
private fun ExamHistoryTimeline(history: List<ExamHistoryPoint>, unit: String) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val lineColor = MaterialTheme.colorScheme.outlineVariant
    val dotColor = MaterialTheme.colorScheme.primary
    Column {
        history.forEachIndexed { index, point ->
            Row(Modifier.fillMaxWidth().height(IntrinsicSize.Min), horizontalArrangement = Arrangement.spacedBy(spacing.space8)) {
                Canvas(Modifier.width(dimensions.iconSmall).fillMaxHeight()) {
                    val anchor = spacing.space8.toPx()
                    drawLine(lineColor, Offset(size.width / 2, if (index == 0) anchor else 0f),
                        Offset(size.width / 2, if (index == history.lastIndex) anchor else size.height), dimensions.strokeThin.toPx())
                    drawCircle(dotColor, dimensions.strokeStrong.toPx(), Offset(size.width / 2, anchor))
                }
                Column(Modifier.weight(1f).padding(bottom = if (index == history.lastIndex) spacing.space4 else spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                    Text(examDate(point.dateTime.toString()), style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    Text("${point.displayValue} $unit", style = MaterialTheme.typography.titleMedium)
                }
            }
        }
    }
}

@Composable
internal fun ExamSectionTitle(text: String) { Text(text, style = MaterialTheme.typography.labelLarge, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.semantics { heading() }) }

@Composable
internal fun ExamEmpty(title: String, message: String, action: @Composable () -> Unit) {
    PartejarCard(Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)) {
            Icon(PartejarIcons.Clinical.calendar, null, Modifier.size(PartejarTheme.dimensions.iconLarge), tint = MaterialTheme.colorScheme.primary)
            Text(title, style = MaterialTheme.typography.titleLarge)
            Text(message, color = MaterialTheme.colorScheme.onSurfaceVariant)
            action()
        }
    }
}

@Composable
internal fun ExamAlert(message: String, error: Boolean) {
    ExamEntrance {
        PartejarAlert(if (error) "Não foi possível concluir" else "Tudo certo", if (error) PartejarAlertTone.ERROR else PartejarAlertTone.SUCCESS,
            modifier = Modifier.semantics { liveRegion = LiveRegionMode.Polite }) { Text(message) }
    }
}

@Composable
internal fun ExamAction(text: String, icon: ImageVector, onClick: () -> Unit, enabled: Boolean = true, saving: Boolean = false, secondary: Boolean = false) {
    PartejarButton(text, onClick, enabled = enabled, isLoading = saving,
        variant = if (secondary) PartejarButtonVariant.SECONDARY else PartejarButtonVariant.PRIMARY,
        leadingIcon = { Icon(icon, null, Modifier.size(PartejarTheme.dimensions.iconMedium)) },
        loadingDescription = "Salvando…", motionEnabled = true)
}
